/**
 * Load-more for collection / search product grids.
 *
 * Click → append next page, sync ?page=
 * Reload with ?page=N:
 *   Collection → prepend pages 1..N-1 onto server page N
 *   Search → keep page 1, append pages 2..N with type=product&section_id
 */
class LoadMore extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this._currentPage = Number(this.getAttribute("current-page")) || 1;
    this._totalPages = Number(this.getAttribute("total-pages")) || 1;
    this._pageSize = Number(this.getAttribute("page-size")) || 0;
    this._totalProducts = Number(this.getAttribute("total-products")) || 0;
    this._countTemplate = this.getAttribute("count-template") || "";
    this._sectionId = this.getAttribute("section-id") || this._sectionIdFromDom();
    this._isSearch = location.pathname.includes("/search");
    this._loading = false;

    this.addEventListener("click", (event) => {
      if (event.target.closest("button")) {
        event.preventDefault();
        this._loadNextPage();
      }
    });

    const urlPage = this._pageFromUrl();
    if (urlPage > 1) {
      this._restore(urlPage);
    } else {
      this._syncUrl(this._currentPage);
      this._updateCount();
    }
  }

  _pageFromUrl() {
    const page = Number(new URL(location.href).searchParams.get("page"));
    if (!Number.isFinite(page) || page < 1) return 1;
    return Math.min(page, this._totalPages);
  }

  _sectionIdFromDom() {
    return this.closest(".shopify-section")?.id?.replace("shopify-section-", "") || "";
  }

  _productList() {
    return (
      this.closest(".collection__inner")?.querySelector("product-list") ||
      this.closest("search-result-panel")?.querySelector("product-list") ||
      this.closest(".collection")?.querySelector("product-list") ||
      null
    );
  }

  _button() {
    return this.querySelector("button");
  }

  async _loadNextPage() {
    if (this._loading || this._currentPage >= this._totalPages) return;

    const productList = this._productList();
    if (!productList || !this._sectionId) return;

    const nextPage = this._currentPage + 1;

    await this._runBusy(null, async () => {
      const { nodes, meta } = await this._fetchPage(nextPage);
      if (!nodes.length) return;

      this._showNodes(nodes);
      productList.append(...nodes);
      productList.reveal?.();

      this._currentPage = nextPage;
      this._applyMeta(meta);
      this._finishPageUpdate();
    });
  }

  async _restore(targetPage) {
    const productList = this._productList();
    if (!productList || !this._sectionId || targetPage <= 1) return;

    const collection = this.closest(".collection");

    await this._runBusy(collection, async () => {
      if (this._isSearch) {
        await this._restoreSearch(productList, targetPage);
      } else {
        await this._restoreCollection(productList, targetPage);
      }

      this._finishPageUpdate();
      productList.reveal?.();
    });
  }

  /** Search: page 1 stays in the grid; append 2..N */
  async _restoreSearch(productList, targetPage) {
    const renderedPage = Number(this.getAttribute("current-page")) || 1;

    if (renderedPage !== 1 || !productList.childElementCount) {
      const { nodes, meta } = await this._fetchPage(1);
      if (!nodes.length) return;

      this._showNodes(nodes);
      productList.replaceChildren(...nodes);
      this._applyMeta(meta);
    }

    this._currentPage = 1;

    for (let page = 2; page <= targetPage; page += 1) {
      const { nodes, meta } = await this._fetchPage(page);
      if (!nodes.length) continue;

      this._showNodes(nodes);
      productList.append(...nodes);
      this._currentPage = page;
      this._applyMeta(meta);
    }
  }

  /** Collection: server rendered page N — prepend 1..N-1 */
  async _restoreCollection(productList, targetPage) {
    const anchor = productList.firstElementChild;
    const fragment = document.createDocumentFragment();

    for (let page = 1; page < targetPage; page += 1) {
      const { nodes } = await this._fetchPage(page);
      if (!nodes.length) continue;

      this._showNodes(nodes);
      nodes.forEach((node) => fragment.appendChild(node));
    }

    if (fragment.childNodes.length) {
      productList.prepend(fragment);
    }

    this._currentPage = targetPage;

    requestAnimationFrame(() => {
      anchor?.scrollIntoView({ block: "start", behavior: "auto" });
    });
  }

  _pageUrl(page) {
    const url = new URL(location.href);
    url.searchParams.set("page", String(page));
    url.searchParams.delete("section_id");
    if (this._isSearch) url.searchParams.set("type", "product");
    url.hash = "";
    return url;
  }

  async _fetchPage(page) {
    const url = this._pageUrl(page);
    if (this._sectionId) url.searchParams.set("section_id", this._sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      const section =
        doc.getElementById(`shopify-section-${this._sectionId}`) ||
        doc.querySelector(".shopify-section");
      const panel =
        section?.querySelector("#main-search-results-product") ||
        section?.querySelector('search-result-panel[id*="product"]');
      const scope = panel || section || doc;
      const remoteList = scope.querySelector("product-list");
      const remoteLoadMore = scope.querySelector("load-more");

      if (!remoteList) return { nodes: [], meta: null };

      return {
        nodes: [...remoteList.children].map((child) => document.importNode(child, true)),
        meta: remoteLoadMore,
      };
    } catch (error) {
      console.error("[load-more]", error);
      return { nodes: [], meta: null };
    }
  }

  _showNodes(nodes) {
    nodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      node.removeAttribute("reveal-on-scroll");
      node.style.opacity = "1";
      node.querySelectorAll("[reveal-on-scroll]").forEach((el) => {
        el.removeAttribute("reveal-on-scroll");
        el.style.opacity = "1";
      });
    });
  }

  _applyMeta(remoteLoadMore) {
    if (!remoteLoadMore) return;
    this._totalPages = Number(remoteLoadMore.getAttribute("total-pages")) || this._totalPages;
    this._totalProducts = Number(remoteLoadMore.getAttribute("total-products")) || this._totalProducts;
    this._pageSize = Number(remoteLoadMore.getAttribute("page-size")) || this._pageSize;
  }

  _finishPageUpdate() {
    this._syncUrl(this._currentPage);
    this._updateNextButton();
    this._updateCount();
    // WISHLIST-TOGGLE: Swym re-init event. To disable, comment out the line below.
    document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
  }

  _updateNextButton() {
    const hasMore = this._currentPage < this._totalPages;
    let button = this._button();

    if (hasMore && !button) {
      button = document.createElement("button");
      button.type = "button";
      button.className = "load-more__button button button--outline";
      button.innerHTML = `<button-content class="button__content">${this.dataset.buttonLabel || "Load more"}</button-content>`;
      this.appendChild(button);
    }

    if (button) button.hidden = !hasMore;
  }

  _syncUrl(page) {
    const url = new URL(location.href);
    if (page > 1) url.searchParams.set("page", String(page));
    else url.searchParams.delete("page");
    history.replaceState({}, "", url.toString());
    this.setAttribute("current-page", String(page));
  }

  _updateCount() {
    const el = this.querySelector("[data-load-more-count]");
    if (!el || !this._countTemplate) return;

    const shown = Math.min(this._currentPage * this._pageSize, this._totalProducts);
    el.textContent = this._countTemplate
      .replace("__SHOWN__", String(shown))
      .replace("__TOTAL__", String(this._totalProducts));
  }

  async _runBusy(collection, task) {
    this._loading = true;
    this._setBusy(true);
    collection?.classList.add("collection--restoring-pages");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      await task();
    } finally {
      this._loading = false;
      this._setBusy(false);
      collection?.classList.remove("collection--restoring-pages");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }

  _setBusy(busy) {
    this.toggleAttribute("aria-busy", busy);
    this._button()?.setAttribute("aria-busy", busy ? "true" : "false");
  }
}

if (!customElements.get("load-more")) {
  customElements.define("load-more", LoadMore);
}
